/**
 * Сервис для локального хранения данных с использованием IndexedDB
 */

const DB_NAME = 'numbermania-offline';
const DB_VERSION = 1;
const STORE_NAME = 'pending-requests';
const GAMES_STORE = 'games-cache';

interface PendingRequest {
  id: string;
  method: string;
  url: string;
  data: any;
  headers: Record<string, string>;
  timestamp: number;
  retries: number;
}

interface GameCache {
  gameId: string;
  data: any;
  timestamp: number;
}

class LocalStorageService {
  private db: IDBDatabase | null = null;
  private initPromise: Promise<void> | null = null;

  private async init(): Promise<void> {
    if (this.db) return;
    if (this.initPromise) return this.initPromise;

    this.initPromise = new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        this.db = request.result;
        resolve();
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;

        // Создаем хранилище для очереди запросов
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          const requestStore = db.createObjectStore(STORE_NAME, {
            keyPath: 'id',
            autoIncrement: false,
          });
          requestStore.createIndex('timestamp', 'timestamp', { unique: false });
        }

        // Создаем хранилище для кэша игр
        if (!db.objectStoreNames.contains(GAMES_STORE)) {
          const gamesStore = db.createObjectStore(GAMES_STORE, {
            keyPath: 'gameId',
          });
          gamesStore.createIndex('timestamp', 'timestamp', { unique: false });
        }
      };
    });

    return this.initPromise;
  }

  // Работа с очередью запросов
  async addPendingRequest(request: Omit<PendingRequest, 'id' | 'timestamp' | 'retries'>): Promise<string> {
    await this.init();
    const id = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const pendingRequest: PendingRequest = {
      id,
      ...request,
      timestamp: Date.now(),
      retries: 0,
    };

    return new Promise((resolve, reject) => {
      if (!this.db) {
        reject(new Error('Database not initialized'));
        return;
      }

      const transaction = this.db.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.add(pendingRequest);

      request.onsuccess = () => resolve(id);
      request.onerror = () => reject(request.error);
    });
  }

  async getPendingRequests(): Promise<PendingRequest[]> {
    await this.init();
    return new Promise((resolve, reject) => {
      if (!this.db) {
        reject(new Error('Database not initialized'));
        return;
      }

      const transaction = this.db.transaction([STORE_NAME], 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.getAll();

      request.onsuccess = () => resolve(request.result || []);
      request.onerror = () => reject(request.error);
    });
  }

  async removePendingRequest(id: string): Promise<void> {
    await this.init();
    return new Promise((resolve, reject) => {
      if (!this.db) {
        reject(new Error('Database not initialized'));
        return;
      }

      const transaction = this.db.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.delete(id);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  async updatePendingRequest(id: string, updates: Partial<PendingRequest>): Promise<void> {
    await this.init();
    return new Promise((resolve, reject) => {
      if (!this.db) {
        reject(new Error('Database not initialized'));
        return;
      }

      const transaction = this.db.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const getRequest = store.get(id);

      getRequest.onsuccess = () => {
        const request = getRequest.result;
        if (!request) {
          reject(new Error('Request not found'));
          return;
        }

        const updated = { ...request, ...updates };
        const putRequest = store.put(updated);
        putRequest.onsuccess = () => resolve();
        putRequest.onerror = () => reject(putRequest.error);
      };

      getRequest.onerror = () => reject(getRequest.error);
    });
  }

  // Работа с кэшем игр
  async cacheGame(gameId: string, data: any): Promise<void> {
    await this.init();
    return new Promise((resolve, reject) => {
      if (!this.db) {
        reject(new Error('Database not initialized'));
        return;
      }

      const transaction = this.db.transaction([GAMES_STORE], 'readwrite');
      const store = transaction.objectStore(GAMES_STORE);
      const cache: GameCache = {
        gameId,
        data,
        timestamp: Date.now(),
      };
      const request = store.put(cache);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  async getCachedGame(gameId: string): Promise<any | null> {
    await this.init();
    return new Promise((resolve, reject) => {
      if (!this.db) {
        reject(new Error('Database not initialized'));
        return;
      }

      const transaction = this.db.transaction([GAMES_STORE], 'readonly');
      const store = transaction.objectStore(GAMES_STORE);
      const request = store.get(gameId);

      request.onsuccess = () => {
        const result = request.result;
        if (result && Date.now() - result.timestamp < 5 * 60 * 1000) {
          // Кэш действителен 5 минут
          resolve(result.data);
        } else {
          resolve(null);
        }
      };
      request.onerror = () => reject(request.error);
    });
  }

  async clearCache(): Promise<void> {
    await this.init();
    return new Promise((resolve, reject) => {
      if (!this.db) {
        reject(new Error('Database not initialized'));
        return;
      }

      const transaction = this.db.transaction([GAMES_STORE], 'readwrite');
      const store = transaction.objectStore(GAMES_STORE);
      const request = store.clear();

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }
}

export const localStorageService = new LocalStorageService();

